/**
 * US States and territories constant for use in dropdowns and forms
 */
export const US_STATES = [
	{ value: 'AL', label: 'Alabama' },
	{ value: 'AK', label: 'Alaska' },
	{ value: 'AZ', label: 'Arizona' },
	{ value: 'AR', label: 'Arkansas' },
	{ value: 'CA', label: 'California' },
	{ value: 'CO', label: 'Colorado' },
	{ value: 'CT', label: 'Connecticut' },
	{ value: 'DE', label: 'Delaware' },
	{ value: 'DC', label: 'District of Columbia' },
	{ value: 'FL', label: 'Florida' },
	{ value: 'GA', label: 'Georgia' },
	{ value: 'HI', label: 'Hawaii' },
	{ value: 'ID', label: 'Idaho' },
	{ value: 'IL', label: 'Illinois' },
	{ value: 'IN', label: 'Indiana' },
	{ value: 'IA', label: 'Iowa' },
	{ value: 'KS', label: 'Kansas' },
	{ value: 'KY', label: 'Kentucky' },
	{ value: 'LA', label: 'Louisiana' },
	{ value: 'ME', label: 'Maine' },
	{ value: 'MD', label: 'Maryland' },
	{ value: 'MA', label: 'Massachusetts' },
	{ value: 'MI', label: 'Michigan' },
	{ value: 'MN', label: 'Minnesota' },
	{ value: 'MS', label: 'Mississippi' },
	{ value: 'MO', label: 'Missouri' },
	{ value: 'MT', label: 'Montana' },
	{ value: 'NE', label: 'Nebraska' },
	{ value: 'NV', label: 'Nevada' },
	{ value: 'NH', label: 'New Hampshire' },
	{ value: 'NJ', label: 'New Jersey' },
	{ value: 'NM', label: 'New Mexico' },
	{ value: 'NY', label: 'New York' },
	{ value: 'NC', label: 'North Carolina' },
	{ value: 'ND', label: 'North Dakota' },
	{ value: 'OH', label: 'Ohio' },
	{ value: 'OK', label: 'Oklahoma' },
	{ value: 'OR', label: 'Oregon' },
	{ value: 'PA', label: 'Pennsylvania' },
	{ value: 'PR', label: 'Puerto Rico' },
	{ value: 'RI', label: 'Rhode Island' },
	{ value: 'SC', label: 'South Carolina' },
	{ value: 'SD', label: 'South Dakota' },
	{ value: 'TN', label: 'Tennessee' },
	{ value: 'TX', label: 'Texas' },
	{ value: 'UT', label: 'Utah' },
	{ value: 'VT', label: 'Vermont' },
	{ value: 'VI', label: 'Virgin Islands' },
	{ value: 'VA', label: 'Virginia' },
	{ value: 'WA', label: 'Washington' },
	{ value: 'WV', label: 'West Virginia' },
	{ value: 'WI', label: 'Wisconsin' },
	{ value: 'WY', label: 'Wyoming' },
	{ value: 'GU', label: 'Guam' },
	{ value: 'AS', label: 'American Samoa' },
	{ value: 'MP', label: 'Northern Mariana Islands' },
] as const;

export type USStateCode = (typeof US_STATES)[number]['value'];
export type USStateName = (typeof US_STATES)[number]['label'];

/**
 * The military "state" codes AA/AE/AP (Armed Forces Americas/Europe/Pacific) —
 * APO/FPO mailing designations, not licensing jurisdictions. We KEEP their
 * registry rows on file (they're real records — mostly DEA registrations with a
 * military address) but do NOT surface them as filterable "states" (PD-14528).
 */
export const MILITARY_STATE_CODES: ReadonlyArray<string> = ['AA', 'AE', 'AP'];

/**
 * INGESTION whitelist — the codes we accept INTO the registry: the 50 states +
 * DC, the inhabited US territories (PR/VI/GU/AS/MP, all in US_STATES with a
 * humanized label), AND the military codes. Used to drop out-of-range codes at
 * ingestion (PD-14489) — non-US values (NPPES 'ZZ', Canadian 'ON'/'QC',
 * truncated foreign codes) never enter. Military codes are intentionally kept so
 * we retain that data on file for any future customer that needs it.
 */
export const US_JURISDICTION_CODES: ReadonlyArray<string> = [
	...US_STATES.map((s) => s.value),
	...MILITARY_STATE_CODES,
];

/**
 * UI/FILTER-facing jurisdictions — the humanized states + territories only,
 * EXCLUDING the military codes. Use this (not US_JURISDICTION_CODES) to build
 * the registry State-filter dropdown so AA/AE/AP stay out of the UI while their
 * data remains in the registry (PD-14528).
 */
export const US_FILTER_JURISDICTION_CODES: ReadonlyArray<string> =
	US_STATES.map((s) => s.value);

const US_JURISDICTION_SET = new Set<string>(US_JURISDICTION_CODES);

/**
 * True when `code` is a valid US jurisdiction (case/space-insensitive).
 * Non-US / corrupted codes return false so callers can drop or hide them.
 */
export function isUsJurisdictionCode(code: string | null | undefined): boolean {
	if (code == null) return false;
	return US_JURISDICTION_SET.has(code.trim().toUpperCase());
}

/**
 * Get state name by code
 */
export function getStateName(code: string): string | undefined {
	return US_STATES.find((state) => state.value === code)?.label;
}

/**
 * Get state code by name
 */
export function getStateCode(name: string): string | undefined {
	return US_STATES.find((state) => state.label === name)?.value;
}
